import {
  AbortError,
  type HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  type ILogger,
  LogLevel,
} from "@microsoft/signalr";
import { useCallback, useEffect, useId, useState } from "react";

import { useAccessTokenContext } from "@/providers/AccessTokenProvider";
import { useConfig } from "@/providers/config/useConfig";

type SignalRHub = "community-feed-hub" | "community-feed-detail-hub" | "notification-hub" | "global-hub" | "chat-hub";

interface ConnectionEntry {
  connection: HubConnection;
  started: Promise<void>;
  listeners: Set<string>;
}

// A raw token replacement or a new SDK session cannot reuse the previous
// credential's accessTokenFactory, even when both subscribe to the same URL.
const scopes = new WeakMap<object, Map<string, ConnectionEntry>>();

const signalRLogger: ILogger = {
  log(logLevel, message) {
    if (logLevel < LogLevel.Warning) {
      return;
    }

    const sanitizedMessage = message
      .replace(/([?&](?:access_token|id|userId)=)[^&\s'"<>]*/gi, "$1[REDACTED]")
      .replace(/(Bearer\s+)[^\s,'"]+/gi, "$1[REDACTED]");
    const formattedMessage = `[SignalR] ${sanitizedMessage}`;

    if (logLevel >= LogLevel.Error) {
      console.error(formattedMessage);
    } else {
      console.warn(formattedMessage);
    }
  },
};

function subscribe(
  scope: object,
  url: string,
  id: string,
  getAccessToken: () => Promise<string>,
): {
  entry: ConnectionEntry;
  release: () => void;
} {
  let connections = scopes.get(scope);
  if (!connections) {
    connections = new Map();
    scopes.set(scope, connections);
  }
  let entry = connections.get(url);
  if (
    !entry ||
    entry.connection.state === HubConnectionState.Disconnected ||
    entry.connection.state === HubConnectionState.Disconnecting
  ) {
    const connection = new HubConnectionBuilder()
      .withUrl(url, { accessTokenFactory: getAccessToken, withCredentials: true })
      .configureLogging(signalRLogger)
      .withAutomaticReconnect()
      .build();
    entry = { connection, started: connection.start(), listeners: new Set() };
    connections.set(url, entry);
  }
  entry.listeners.add(id);
  const subscribedEntry = entry;
  const subscribedConnections = connections;

  return {
    entry,
    release: () => {
      subscribedEntry.listeners.delete(id);
      // Preserve the existing grace period for Strict Mode and short route remounts.
      setTimeout(() => {
        if (subscribedEntry.listeners.size === 0) {
          void subscribedEntry.connection.stop().catch(console.error);
          if (subscribedConnections.get(url) === subscribedEntry) {
            subscribedConnections.delete(url);
          }
        }
      }, 50);
    },
  };
}

export function useSignalRHub(
  hub: SignalRHub,
  opts?: { query?: string; disabled?: boolean },
): { signalRConnection: HubConnection | null } {
  const id = useId();
  const coreApiUri = useConfig("newCoreApiRootUri");
  const { connectionScope, getAccessToken } = useAccessTokenContext();
  const disabled = opts?.disabled ?? false;
  const query = opts?.query;
  const url = `${coreApiUri}/hubs/${hub}${query ? "?" + query : ""}`;
  const [resolved, setResolved] = useState<{ scope: object; url: string; connection: HubConnection }>();

  useEffect(() => {
    if (disabled) {
      return;
    }
    let cancelled = false;
    const { entry, release } = subscribe(connectionScope, url, id, async () => {
      const token = await getAccessToken();
      if (!token) {
        throw new Error("Token is not available");
      }

      return token;
    });
    void entry.started
      .then(() => {
        if (!cancelled) {
          setResolved({ scope: connectionScope, url, connection: entry.connection });
        }
      })
      .catch((error: unknown) => {
        if (!(error instanceof AbortError)) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          signalRLogger.log(LogLevel.Error, `Could not start SignalR on ${url}\n${errorMessage}`);
        }
      });

    return () => {
      cancelled = true;
      release();
    };
  }, [connectionScope, getAccessToken, disabled, url, id]);

  const resolvedScope = resolved?.scope;
  const resolvedUrl = resolved?.url;
  const connection = resolved?.connection;
  const signalRConnection =
    !disabled && resolvedScope === connectionScope && resolvedUrl === url ? (connection ?? null) : null;

  return { signalRConnection };
}

export function useSignalRSubscription(
  signalRHub: HubConnection | null,
  methodName: string,
  method: (...args: any[]) => void,
): void {
  useEffect(() => {
    if (!signalRHub) {
      return;
    }

    // on() can be registered in any connection state; the handler persists across
    // automatic reconnects and is invoked whenever a matching message arrives.
    signalRHub.on(methodName, method);

    return () => {
      signalRHub.off(methodName, method);
    };
  }, [signalRHub, method, methodName]);
}

export function useSignalRInvocation<TReturnType = never>(
  signalRHub: HubConnection | null,
  methodName: string,
): { invoke: (...args: any[]) => Promise<TReturnType | undefined> } {
  const invoke = useCallback(
    async (...args: any[]) => {
      try {
        if (!signalRHub || signalRHub.state !== HubConnectionState.Connected) {
          throw new Error("SignalR connection is not established");
        }

        const data = await signalRHub.invoke<TReturnType>(methodName, ...args);

        return data;
      } catch (e) {
        console.error("Could not invoke SignalR", e);
      }
    },
    [signalRHub, methodName],
  );

  return { invoke };
}
